import { Interval } from '@/model';
import { functional } from '@/util/functional-iterator';

export interface SelectionHandler {
  deselect(): void;
  deregister(): void;
}
export interface SelectionNode {
  readonly element: Element;
  readonly context: { [k: string]: string | undefined };
  readonly startOffset?: number;
  readonly endOffset?: number;
}
export interface NodeSelection {
  readonly range: Range;
  readonly nodes: readonly SelectionNode[];
}

export function registerSelectionHandler(
  doc: Document,
  onChange: (sel?: NodeSelection) => void,
  root: (elem: Element) => boolean,
  node: (elem: Element) => boolean,
  ...contextAttributes: string[]
): SelectionHandler {
  const handler = (_event: Event) => {
    const range = selectedRange(doc);
    if (range) {
      const nodes = selection(range, root, node, ...contextAttributes);
      if (nodes.length > 0) {
        onChange({ range, nodes });
        return;
      }
    }
    onChange(undefined);
  };
  doc.addEventListener('selectionchange', handler);
  return {
    deselect: () => doc.getSelection()?.removeAllRanges(),
    deregister: () => doc.removeEventListener('selectionchange', handler),
  };
}

function selectedRange(doc: Document): Range | undefined {
  const sel = doc.getSelection();
  if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
    // TODO multi-range
    return sel.getRangeAt(0);
  }
  return undefined;
}

function selection(
  range: Range,
  root: (elem: Element) => boolean,
  node: (elem: Element) => boolean,
  ...contextAttributes: string[]
): SelectionNode[] {
  const rootElement = findParentElement(range.commonAncestorContainer, root);

  if (rootElement) {
    const { startContainer, endContainer } = range;
    const startElement = boundary(startContainer, node);
    const endElement = boundary(endContainer, node);
    const elements = (startElement === endElement
      ? [startElement]
      : [startElement, ...nodesBetween(startElement || startContainer, endElement || endContainer), endElement])
      .filter((n) => n instanceof Element)
      .map((n) => n as Element)
      .filter(node);

    const nodes = (elements as Element[]).map((element) => {
      const context = contextAttributes.reduce((acc, attr) => ({ ...acc, [attr]: contextAttribute(element, attr) }), {});
      const startOffset = element === startElement ? range.startOffset : undefined;
      const endOffset = element === endElement ? range.endOffset : undefined;
      return {
        element, context, startOffset, endOffset,
      };
    });
    return nodes;
  }
  return [];
}

function boundary(n: Node, p?: (elem: Element) => boolean) {
  let e: Element | undefined;
  if (n.nodeType === Node.TEXT_NODE && n.parentElement) {
    e = n.parentElement;
  } else if (e instanceof Element) {
    e = n as Element;
  }
  return (e && p && p(e)) ? e : undefined;
}

function contextAttribute(node: Element, qualifiedName: string) {
  return functional(parentElements(node))
    .prepend([node])
    .map((p) => p?.attributes.getNamedItem(qualifiedName)?.value || undefined)
    .find((p) => !!p);
}

function* nodesBetween(from: Node, to: Node) {
  if (from !== to) {
    let n: Node | undefined = from;
    while (n && n !== to) {
      n = nextNode(n);
      if (n && n !== to) {
        yield n;
      }
    }
  }
}

function nextNode(node: Node): Node | undefined {
  if (node.hasChildNodes()) {
    return node.firstChild || undefined;
  }
  let n: Node | undefined = node;
  while (n && !n.nextSibling) {
    n = n.parentNode || undefined;
  }
  if (!n) {
    return undefined;
  }
  return n.nextSibling || undefined;
}

function* parentElements(node: Node): Generator<Element, void, unknown> {
  if (node.parentElement) {
    yield node.parentElement;
    yield* parentElements(node.parentElement);
  }
}

function findParentElement(node: Node, p: (elem: Element) => boolean): Element | undefined {
  if (node instanceof Element) {
    if (p(node)) {
      return node;
    }
  }
  if (node.parentNode) {
    return findParentElement(node.parentNode, p);
  }
  return undefined;
}

export function collapse(intervals: readonly Interval[]): Interval[] {
  const edges: Set<number> = new Set();
  for (const interval of intervals) {
    edges.add(interval.from);
    edges.add(interval.to);
  }
  const sorted = [...edges].sort((a, b) => a - b);
  const result: Interval[] = [];
  for (let i = 1; i < sorted.length; i++) {
    result.push({ from: sorted[i - 1], to: sorted[i] });
  }
  return result;
}

export type TypedInterval<T> = Interval & T
export type GroupedInterval<T> = Interval & { group: TypedInterval<T>[] }

export function groups<T>(intervals: readonly TypedInterval<T>[]): GroupedInterval<T>[] {
  return collapse(intervals).map((inter) => ({
    ...inter,
    group: intervals.filter((i) => intersects(inter, i)),
  }));
}

export function intersects(a: Interval, b: Interval): boolean {
  return a.from < b.to && b.from < a.to;
}

export function symmetricDifference(a: Interval, b: Interval, mergeDiff = true): Interval[] {
  if (!intersects(a, b)) {
    return [a, b];
  } else {
    const diff = [
      ...removeInterval(a, b),
      ...removeInterval(b, a),
    ];
    return mergeDiff ? merge(diff) : diff;
  }
}

export function removeInterval(interval: Interval, remove: Interval): Interval[] {
  if (intersects(interval, remove)) {
    const clipedIntervals: Interval[] = [];
    if (interval.from < remove.from) {
      clipedIntervals.push({
        from: interval.from,
        to: remove.from,
      });
    }
    if (interval.to > remove.to) {
      clipedIntervals.push({
        from: remove.to,
        to: interval.to,
      });
    }
    return clipedIntervals;
  }
  return [interval];
}

export function merge<T>(intervals: TypedInterval<T>[]): TypedInterval<T>[] {
  if (intervals.length === 0) {
    return [];
  }

  const sorted = intervals.sort((a, b) => a.from - b.from);
  let currentInterval = { ...sorted[0] };
  const result = [] as TypedInterval<T>[];
  sorted.forEach((interval) => {
    if (currentInterval.to >= interval.from) {
      currentInterval = {
        ...currentInterval,
        to: Math.max(currentInterval.to, interval.to),
      };
    } else {
      result.push({ ...currentInterval });
      currentInterval = { ...interval };
    }
  });
  return [...result, currentInterval];
}
